import Icon1 from "@assets/icons/Icon";
import theme from "@theme/index";
import { CSSProperties, JSX, useState, useCallback } from "react";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

interface ButtonProps {
  label: string;
  onClick?: () => void;
  style?: CSSProperties;
  variant?: "small" | "medium" | "large";
  disabled?: boolean;
  children?: JSX.Element | JSX.Element[];
  closeRef?: React.MutableRefObject<(() => void) | null>;
}

export default function MenuButton({
  label,
  onClick,
  style,
  variant = "medium",
  disabled = false,
  children,
  closeRef,
}: ButtonProps) {
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    if (closeRef) closeRef.current = () => setOpen(false);
  }, [closeRef]);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState<boolean>(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // Close on click outside
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (open) {
      setVisible(true);
    } else {
      const timeout = setTimeout(() => setVisible(false), 200);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  // Reposition once the portal menu is mounted
  const menuRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      (menuRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      if (!node || !buttonRef.current) return;
      const btnRect = buttonRef.current.getBoundingClientRect();
      const menuWidth = node.offsetWidth;
      const menuHeight = node.scrollHeight;
      const pad = 8;

      let top = btnRect.bottom;
      if (top + menuHeight > window.innerHeight - pad) {
        top = Math.max(pad, window.innerHeight - pad - menuHeight);
      }

      // Align to button's left, but flip to right-align if it would overflow the viewport
      let left = btnRect.left;
      if (left + menuWidth > window.innerWidth - pad) {
        left = Math.max(pad, btnRect.right - menuWidth);
      }

      setMenuPos({ top, left });
    },
    [open],
  );

  return (
    <div className="relative h-fit">
      <div
        ref={buttonRef}
        onClick={() => setOpen(!open)}
        className={`
          hover:ring-2 hover:ring-neutral-300

         w-fit rounded-xl shadow-md
        ${disabled ? "bg-neutral-300 text-neutral-500 cursor-not-allowed" : "bg-black text-white cursor-pointer"} flex flex-row items-center justify-center`}
      >
        <div className="w-fit px-6 py-3 font-medium text-h2">{label}</div>
        <div className="self-stretch border-r-1 border-neutral-400"></div>
        <div className="p-3 flex flex-col justify-center items-center">
          <Icon1 name="down" size={16} color="white" />
        </div>
      </div>

      {open && createPortal(
        <div
          ref={menuRefCallback}
          className={`
          ${visible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-10 scale-90"}
        fixed z-50 w-fit min-w-300 transition-all duration-200 flex flex-col text-shadow_50 border-1 border-shadow_10 bg-white shadow-xl rounded-lg`}
          style={{ top: menuPos.top, left: menuPos.left }}
          onClick={() => setOpen(false)}
        >
          {children}
        </div>,
        document.body
      )}
    </div>
  );
}
